import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button, { ButtonTypes } from 'components/common/button';
import {
  appendArrayItem,
  appendRowKeys,
  applyFieldChange,
  buildRowKeys,
  defaultValueForNode,
  dependencyParentLabel,
  dependentParentValue,
  extractSchemaProperties,
  fieldControlId,
  findMissingRequired,
  formatProposedActionArguments,
  initialFormValuesFor,
  isDependentArray,
  isEngaged,
  iso8601ToDatetimeLocal,
  nestedRowKeys,
  normalizeFormValuesForSubmit,
  optionKey,
  parseProposedActionArguments,
  removeArrayItem,
  removeRowKeys,
  rowKeysFor,
  sameOptionValue,
  visibleOptionsFor,
} from 'components/customer/team_assist/team_assist_proposed_action_helpers';

function ProposedActionCard({ execution, isExecuting, isRejected, proposedAction = {}, onExecute, onReject }) {
  const { t } = useTranslation();
  const [errorDetailsExpanded, setErrorDetailsExpanded] = useState(false);
  // The tool name is `<config digest>-<source>-<action>`, so it means nothing to
  // an agent — supernova sends the agent-facing title with the proposal. Older
  // proposals carry no title, so those still show the raw name.
  const displayName = proposedAction.toolDisplayName || proposedAction.toolName || '';
  const nodes = extractSchemaProperties(proposedAction.inputSchema);
  const [formValues, setFormValues] = useState(() => initialFormValuesFor(proposedAction.arguments, nodes));
  // Row identity for every array in the form, so a row's own component instance
  // (and the expanded state its optional sections hold) follows its data rather
  // than its position. See buildRowKeys.
  const [rowKeys, setRowKeys] = useState(() => buildRowKeys(nodes, formValues));
  const isExecuted = Boolean(execution);
  const isLocked = isExecuted || isRejected;
  const missingRequired = findMissingRequired(formValues, nodes);
  const canExecute = missingRequired.length === 0;

  // A single change handler drives every leaf regardless of nesting: the value
  // is written through its path, then cascading resets fire across the whole
  // tree (clearing a stale dependent leaf at any depth and resetting a dependent
  // array to one blank row). Every change goes through this, because a cascading
  // parent can itself be nested — e.g. an orderId inside a wrapping `input`
  // object, alongside the line items that depend on it.
  const handleChange = (path, value) => {
    setFormValues(prev => applyFieldChange(prev, nodes, path, value));
  };
  const handleArrayAppend = (path, itemNode) => {
    setFormValues(prev => appendArrayItem(prev, path, defaultValueForNode(itemNode)));
    setRowKeys(prev => appendRowKeys(prev, path));
  };
  const handleArrayRemove = (path, index) => {
    setFormValues(prev => removeArrayItem(prev, path, index));
    setRowKeys(prev => removeRowKeys(prev, path, index));
  };

  return (
    <ProposedActionContainer data-aid="teamAssistProposedActionCard">
      <ProposedActionHeader>
        <ProposedActionIcon className="fa fa-exclamation-triangle" />
        <ProposedActionTitleWrap>
          <ProposedActionLabel>{t('Action awaiting review')}</ProposedActionLabel>
          {displayName && <ProposedActionToolName>{displayName}</ProposedActionToolName>}
        </ProposedActionTitleWrap>
      </ProposedActionHeader>
      {nodes.length > 0 ? (
        <ProposedActionForm data-aid="teamAssistProposedActionForm">
          {nodes.map(node => (
            <ProposedActionNode
              disabled={isLocked || isExecuting}
              formValues={formValues}
              key={node.name}
              node={node}
              nodes={nodes}
              onArrayAppend={handleArrayAppend}
              onArrayRemove={handleArrayRemove}
              onChange={handleChange}
              path={[node.name]}
              rowKeys={rowKeys[node.name]}
              value={formValues[node.name]}
            />
          ))}
        </ProposedActionForm>
      ) : (
        // Fallback for proposals emitted before inputSchema was added —
        // keep showing the raw argument JSON so the agent at least sees
        // what would execute.
        <FallbackArgsPreview args={proposedAction.arguments} />
      )}
      {!isLocked && (
        <ProposedActionActions>
          <Button
            aria-label={t('Execute proposed action')}
            buttonType={ButtonTypes.PRIMARY}
            data-aid="teamAssistProposedActionExecuteButton"
            disabled={isExecuting || !canExecute}
            isSmall
            onClick={() =>
              onExecute(
                nodes.length > 0
                  ? normalizeFormValuesForSubmit(formValues, nodes)
                  : parseProposedActionArguments(proposedAction.arguments)
              )
            }
          >
            {isExecuting ? t('Executing…') : t('Execute')}
          </Button>
          <Button
            aria-label={t('Reject proposed action')}
            buttonType={ButtonTypes.TEXT}
            data-aid="teamAssistProposedActionRejectButton"
            disabled={isExecuting}
            isSmall
            onClick={onReject}
          >
            {t('Reject')}
          </Button>
          {!canExecute && (
            <ProposedActionRequiredNote data-aid="teamAssistProposedActionRequiredNote">
              {t('Fill required fields to execute')}
            </ProposedActionRequiredNote>
          )}
        </ProposedActionActions>
      )}
      {isRejected && !isExecuted && (
        <ProposedActionMutedState data-aid="teamAssistProposedActionRejected">{t('Rejected')}</ProposedActionMutedState>
      )}
      {isExecuted && !execution.isError && (
        <ProposedActionMutedState data-aid="teamAssistProposedActionExecuted">{t('Executed')}</ProposedActionMutedState>
      )}
      {isExecuted && execution.isError && (
        <ProposedActionErrorBlock data-aid="teamAssistProposedActionError">
          <ProposedActionErrorHeader>
            <ProposedActionErrorIcon className="fa fa-exclamation-circle" />
            <ProposedActionErrorText>{t('Action failed')}</ProposedActionErrorText>
            {execution.result && (
              <ProposedActionErrorExpander
                aria-expanded={errorDetailsExpanded}
                data-aid="teamAssistProposedActionErrorToggle"
                onClick={() => setErrorDetailsExpanded(v => !v)}
                type="button"
              >
                {errorDetailsExpanded ? t('Hide error details') : t('Show error details')}
              </ProposedActionErrorExpander>
            )}
          </ProposedActionErrorHeader>
          {errorDetailsExpanded && execution.result && (
            <ProposedActionErrorBody data-aid="teamAssistProposedActionErrorBody">
              {execution.result}
            </ProposedActionErrorBody>
          )}
        </ProposedActionErrorBlock>
      )}
    </ProposedActionContainer>
  );
}

ProposedActionCard.propTypes = {
  execution: PropTypes.shape({
    isError: PropTypes.bool,
    result: PropTypes.string,
  }),
  isExecuting: PropTypes.bool,
  isRejected: PropTypes.bool,
  /** The proposal as it was stored: what to run, how to show it, and the arguments the model chose. */
  proposedAction: PropTypes.shape({
    toolName: PropTypes.string,
    toolDisplayName: PropTypes.string,
    summary: PropTypes.string,
    arguments: PropTypes.any,
    inputSchema: PropTypes.any,
  }),
  onExecute: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
};

export default ProposedActionCard;

// ProposedActionNode renders one schema node (leaf field, object section, or
// array section) at its value `path`, recursing for nested objects/arrays.
function ProposedActionNode({
  disabled,
  nodes,
  formValues,
  hideLabel,
  node,
  onArrayAppend,
  onArrayRemove,
  onChange,
  path,
  rowKeys,
  value,
}) {
  const kind = node.kind || 'field';
  if (kind === 'object') {
    return (
      <ProposedActionSection
        disabled={disabled}
        formValues={formValues}
        node={node}
        nodes={nodes}
        onArrayAppend={onArrayAppend}
        onArrayRemove={onArrayRemove}
        onChange={onChange}
        path={path}
        rowKeys={rowKeys}
        value={value}
      />
    );
  }
  if (kind === 'array') {
    return (
      <ProposedActionArraySection
        disabled={disabled}
        formValues={formValues}
        node={node}
        nodes={nodes}
        onArrayAppend={onArrayAppend}
        onArrayRemove={onArrayRemove}
        onChange={onChange}
        path={path}
        rowKeys={rowKeys}
        value={value}
      />
    );
  }
  // A dependent (dependsOn) leaf names its cascading parent by absolute path, so
  // it resolves against the top-level values wherever the child sits (see
  // dependentParentValue). That covers every shape the backend emits: a parent
  // alongside the child in the same input object; a parent one or more levels up
  // (e.g. an orderId in the input object above a lineItems array whose object
  // items carry the dependent lineItemId, or above a lineItemIds array of bare id
  // scalars); a parent in a sibling object the child is not inside at all; and a
  // plain top-level parent.
  const parentValue = node.dependsOn ? dependentParentValue(node, formValues, path) : null;
  const parentLabel = node.dependsOn ? dependencyParentLabel(nodes, node.dependsOn) : null;
  return (
    <ProposedActionField
      disabled={disabled}
      field={node}
      fieldId={path.join('-')}
      hideLabel={hideLabel}
      onChange={next => onChange(path, next)}
      options={visibleOptionsFor(node, formValues, path)}
      parentLabel={parentLabel}
      parentSelected={!node.dependsOn || (parentValue !== null && parentValue !== undefined && parentValue !== '')}
      value={value}
    />
  );
}

const nodePropTypes = {
  disabled: PropTypes.bool,
  nodes: PropTypes.arrayOf(PropTypes.object),
  formValues: PropTypes.object,
  hideLabel: PropTypes.bool,
  node: PropTypes.object.isRequired,
  onArrayAppend: PropTypes.func.isRequired,
  onArrayRemove: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  path: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  // This branch's slice of the row-key mirror; absent for a branch holding no
  // array (see buildRowKeys).
  rowKeys: PropTypes.object,
  value: PropTypes.any,
};

ProposedActionNode.propTypes = nodePropTypes;

// SectionHeader renders the section title. A required section shows a static
// title with an asterisk; an optional section shows a caret toggle so the agent
// can collapse it. Optional sections are why object/array inputs stay compact
// until the agent chooses to fill them.
function SectionHeader({ collapsible, label, onToggle, open, required }) {
  const { t } = useTranslation();
  if (!collapsible) {
    return (
      <ProposedActionSectionTitle>
        {label}
        {required && <ProposedActionFieldRequired aria-hidden="true"> *</ProposedActionFieldRequired>}
      </ProposedActionSectionTitle>
    );
  }
  return (
    <ProposedActionExpander
      aria-expanded={open}
      data-aid="teamAssistProposedActionSectionToggle"
      onClick={onToggle}
      type="button"
    >
      <i aria-hidden="true" className={`fa fa-caret-${open ? 'down' : 'right'}`} /> {label}{' '}
      <ProposedActionOptionalTag>{t('(optional)')}</ProposedActionOptionalTag>
    </ProposedActionExpander>
  );
}

SectionHeader.propTypes = {
  collapsible: PropTypes.bool,
  label: PropTypes.string,
  onToggle: PropTypes.func,
  open: PropTypes.bool,
  required: PropTypes.bool,
};

// ProposedActionSection flattens an object input into a labeled group of fields.
// A required object is always shown; an optional one is collapsible and starts
// open only when the LLM pre-filled a value inside it.
function ProposedActionSection({
  disabled,
  nodes,
  formValues,
  node,
  onArrayAppend,
  onArrayRemove,
  onChange,
  path,
  rowKeys,
  value,
}) {
  const collapsible = !node.required;
  const [expanded, setExpanded] = useState(() => node.required || isEngaged(node, value));
  const open = collapsible ? expanded : true;
  return (
    <ProposedActionSectionBox data-aid="teamAssistProposedActionSection">
      <SectionHeader
        collapsible={collapsible}
        label={node.label}
        onToggle={() => setExpanded(v => !v)}
        open={open}
        required={node.required}
      />
      {node.description && <ProposedActionFieldDescription>{node.description}</ProposedActionFieldDescription>}
      {open && (
        <ProposedActionSectionBody>
          {node.children.map(child => (
            <ProposedActionNode
              disabled={disabled}
              formValues={formValues}
              key={child.name}
              node={child}
              nodes={nodes}
              onArrayAppend={onArrayAppend}
              onArrayRemove={onArrayRemove}
              onChange={onChange}
              path={[...path, child.name]}
              rowKeys={rowKeys ? rowKeys[child.name] : undefined}
              value={value ? value[child.name] : undefined}
            />
          ))}
        </ProposedActionSectionBody>
      )}
    </ProposedActionSectionBox>
  );
}

ProposedActionSection.propTypes = nodePropTypes;

// ProposedActionArraySection renders a repeatable list input: each item is the
// element schema rendered at its index, with a Remove button, plus an Add button
// that appends a blank item. Object items are flattened inline (their sub-fields
// render directly under the item row) rather than as a nested collapsible.
function ProposedActionArraySection({
  disabled,
  nodes,
  formValues,
  node,
  onArrayAppend,
  onArrayRemove,
  onChange,
  path,
  rowKeys,
  value,
}) {
  const { t } = useTranslation();
  const items = Array.isArray(value) ? value : [];
  const itemKeys = rowKeysFor(rowKeys, items.length);
  const collapsible = !node.required;
  // A dependent array starts open so its seeded/reset row (the one item the
  // agent picks a cascading value in) is visible without expanding first.
  const [expanded, setExpanded] = useState(() => node.required || isDependentArray(node) || isEngaged(node, value));
  const open = collapsible ? expanded : true;
  const itemIsObject = (node.item.kind || 'field') === 'object';
  return (
    <ProposedActionSectionBox data-aid="teamAssistProposedActionArraySection">
      <SectionHeader
        collapsible={collapsible}
        label={node.label}
        onToggle={() => setExpanded(v => !v)}
        open={open}
        required={node.required}
      />
      {node.description && <ProposedActionFieldDescription>{node.description}</ProposedActionFieldDescription>}
      {open && (
        <ProposedActionSectionBody>
          {items.map((item, index) => {
            const itemPath = [...path, index];
            const itemRowKeys = nestedRowKeys(rowKeys, index);
            const caption = `${node.label} #${index + 1}`;
            return (
              <ProposedActionArrayItem data-aid="teamAssistProposedActionArrayItem" key={itemKeys[index]}>
                <ProposedActionArrayItemHeader>
                  {/* A scalar item's control is rendered with hideLabel, so this
                      caption is its only name — bind the two with htmlFor. An
                      object item has no single control to name, and each of its
                      sub-fields carries its own label, so its caption stays a
                      plain span. */}
                  {itemIsObject ? (
                    <ProposedActionArrayItemLabel>{caption}</ProposedActionArrayItemLabel>
                  ) : (
                    <ProposedActionArrayItemLabel as="label" htmlFor={fieldControlId(itemPath.join('-'))}>
                      {caption}
                    </ProposedActionArrayItemLabel>
                  )}
                  <ProposedActionRemoveButton
                    aria-label={t('Remove item')}
                    data-aid="teamAssistProposedActionArrayRemove"
                    disabled={disabled}
                    onClick={() => onArrayRemove(path, index)}
                    type="button"
                  >
                    {t('Remove')}
                  </ProposedActionRemoveButton>
                </ProposedActionArrayItemHeader>
                <ProposedActionSectionBody>
                  {itemIsObject ? (
                    node.item.children.map(child => (
                      <ProposedActionNode
                        disabled={disabled}
                        formValues={formValues}
                        key={child.name}
                        node={child}
                        nodes={nodes}
                        onArrayAppend={onArrayAppend}
                        onArrayRemove={onArrayRemove}
                        onChange={onChange}
                        path={[...itemPath, child.name]}
                        rowKeys={itemRowKeys ? itemRowKeys[child.name] : undefined}
                        value={item ? item[child.name] : undefined}
                      />
                    ))
                  ) : (
                    <ProposedActionNode
                      disabled={disabled}
                      formValues={formValues}
                      hideLabel
                      node={node.item}
                      nodes={nodes}
                      onArrayAppend={onArrayAppend}
                      onArrayRemove={onArrayRemove}
                      onChange={onChange}
                      path={itemPath}
                      rowKeys={itemRowKeys}
                      value={item}
                    />
                  )}
                </ProposedActionSectionBody>
              </ProposedActionArrayItem>
            );
          })}
          <ProposedActionAddButton
            data-aid="teamAssistProposedActionArrayAdd"
            disabled={disabled}
            onClick={() => onArrayAppend(path, node.item)}
            type="button"
          >
            <i aria-hidden="true" className="fa fa-plus" /> {t('Add {{label}}', { label: node.label })}
          </ProposedActionAddButton>
        </ProposedActionSectionBody>
      )}
    </ProposedActionSectionBox>
  );
}

ProposedActionArraySection.propTypes = nodePropTypes;

function ProposedActionField({
  disabled,
  field,
  fieldId,
  hideLabel,
  onChange,
  options,
  parentLabel,
  parentSelected,
  value,
}) {
  const { t } = useTranslation();
  // A path-derived id keeps nested/array-item controls unique — two fields both
  // named "street" in different objects would otherwise collide.
  const key = fieldId || field.name;
  const controlId = fieldControlId(key);
  const dataAid = `teamAssistProposedActionField-${key}`;
  const label = hideLabel ? null : (
    <ProposedActionFieldLabel htmlFor={controlId}>
      {field.label}
      {field.required && <ProposedActionFieldRequired aria-hidden="true"> *</ProposedActionFieldRequired>}
    </ProposedActionFieldLabel>
  );
  const description = field.description ? (
    <ProposedActionFieldDescription>{field.description}</ProposedActionFieldDescription>
  ) : null;

  if (field.type === 'boolean') {
    return (
      <ProposedActionFieldRow data-aid="teamAssistProposedActionField">
        <ProposedActionFieldCheckboxRow>
          <input
            checked={Boolean(value)}
            data-aid={dataAid}
            disabled={disabled}
            id={controlId}
            onChange={e => onChange(e.target.checked)}
            type="checkbox"
          />
          {label}
        </ProposedActionFieldCheckboxRow>
        {description}
      </ProposedActionFieldRow>
    );
  }

  if (field.type === 'enum') {
    // Prefer the resolved labeled options passed by the parent (already
    // filtered to the selected parent for a dependent select) — e.g. an
    // id-typed field the backend turned into a human-readable picker — and fall
    // back to the field's own options, then bare enum values (label === value).
    const selectOptions =
      options || field.options || (field.enumValues || []).map(v => ({ value: v, label: String(v) }));
    // A dependent select stays disabled until its parent is chosen, and
    // surfaces a clear empty state when the chosen parent has no options (e.g.
    // none were enumerated for it) rather than rendering a silent blank.
    const isDependent = Boolean(field.dependsOn);
    const awaitingParent = isDependent && !parentSelected;
    const noOptionsForParent = isDependent && parentSelected && selectOptions.length === 0;
    let placeholder = t('Select…');
    if (awaitingParent && parentLabel) {
      placeholder = t('Select {{parent}} first', { parent: parentLabel });
    } else if (noOptionsForParent) {
      placeholder = parentLabel
        ? t('No options for the selected {{parent}}', { parent: parentLabel })
        : t('No options available');
    }
    // An option's value is an id for a scalar field and the whole entity for an
    // id-typed object input, so the control carries each option's string
    // identity and hands back the value itself — a number stays a number, and
    // two objects never collapse into one another.
    const selected = selectOptions.find(option => sameOptionValue(option.value, value));
    return (
      <ProposedActionFieldRow data-aid="teamAssistProposedActionField">
        {label}
        <ProposedActionFieldSelect
          data-aid={dataAid}
          disabled={disabled || awaitingParent || noOptionsForParent}
          id={controlId}
          onChange={e => {
            const picked = selectOptions.find(option => optionKey(option.value) === e.target.value);
            onChange(picked ? picked.value : '');
          }}
          value={selected ? optionKey(selected.value) : ''}
        >
          <option value="">{placeholder}</option>
          {/*
            option.label is LLM-authored, derived from a third-party app's data.
            Render it as a text child only — never through dangerouslySetInnerHTML
            or a prop the DOM parses as markup. The backend HTML-sanitizes it as a
            second layer, which is why a label can arrive with entities escaped
            (&amp;) and bracketed runs (<size M>) dropped.
          */}
          {selectOptions.map(option => (
            <option key={optionKey(option.value)} value={optionKey(option.value)}>
              {option.label}
            </option>
          ))}
        </ProposedActionFieldSelect>
        {description}
      </ProposedActionFieldRow>
    );
  }

  if (field.type === 'number') {
    return (
      <ProposedActionFieldRow data-aid="teamAssistProposedActionField">
        {label}
        <ProposedActionFieldInput
          data-aid={dataAid}
          disabled={disabled}
          id={controlId}
          onChange={e => {
            const raw = e.target.value;
            if (raw === '') {
              onChange('');
            } else {
              const num = Number(raw);
              onChange(Number.isNaN(num) ? '' : num);
            }
          }}
          type="number"
          value={value == null ? '' : String(value)}
        />
        {description}
      </ProposedActionFieldRow>
    );
  }

  const isDate = field.type === 'date';
  // datetime-local can only render the bare "YYYY-MM-DDThh:mm" shape, so a
  // value prefilled by the LLM in full ISO 8601 must be converted for display.
  let displayValue = value == null ? '' : String(value);
  if (isDate) {
    displayValue = iso8601ToDatetimeLocal(value);
  }
  return (
    <ProposedActionFieldRow data-aid="teamAssistProposedActionField">
      {label}
      <ProposedActionFieldInput
        data-aid={dataAid}
        disabled={disabled}
        id={controlId}
        onChange={e => onChange(e.target.value)}
        type={isDate ? 'datetime-local' : 'text'}
        value={displayValue}
      />
      {description}
    </ProposedActionFieldRow>
  );
}

const optionShape = PropTypes.shape({
  label: PropTypes.string,
  parentValue: PropTypes.string,
  value: PropTypes.any,
});

ProposedActionField.propTypes = {
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    dependsOn: PropTypes.string,
    description: PropTypes.string,
    enumValues: PropTypes.array,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(optionShape),
    required: PropTypes.bool,
    type: PropTypes.oneOf(['boolean', 'date', 'enum', 'number', 'text']).isRequired,
  }).isRequired,
  // Unique control id/data-aid suffix derived from the value path; defaults to
  // field.name for top-level fields.
  fieldId: PropTypes.string,
  // Suppresses the field label (used for scalar array items, which are already
  // labeled by the array section and item row).
  hideLabel: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  // Resolved, parent-filtered options for a dependent select; falls back to
  // field.options when omitted.
  options: PropTypes.arrayOf(optionShape),
  parentLabel: PropTypes.string,
  parentSelected: PropTypes.bool,
  value: PropTypes.any,
};

function FallbackArgsPreview({ args }) {
  const { t } = useTranslation();
  // Expanded by default: on this path the preview is the only view of what
  // Execute sends, so a collapsed blob would let the agent confirm a
  // destructive call they never saw.
  const [expanded, setExpanded] = useState(true);
  const argsPreview = formatProposedActionArguments(args);
  if (!argsPreview) {
    return null;
  }
  return (
    <ProposedActionArgs>
      <ProposedActionExpander
        aria-expanded={expanded}
        data-aid="teamAssistProposedActionArgsToggle"
        onClick={() => setExpanded(v => !v)}
        type="button"
      >
        <i aria-hidden="true" className={`fa fa-caret-${expanded ? 'down' : 'right'}`} />{' '}
        {expanded ? t('Hide arguments') : t('Show arguments')}
      </ProposedActionExpander>
      {expanded && <ProposedActionArgsBlock>{argsPreview}</ProposedActionArgsBlock>}
    </ProposedActionArgs>
  );
}

FallbackArgsPreview.propTypes = {
  args: PropTypes.any,
};

const ProposedActionContainer = styled.div`
  background: ${p => p.theme.colors.yellow100};
  border: 1px solid ${p => p.theme.colors.yellow300};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  padding: 10px 12px;
`;

const ProposedActionHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 8px;
`;

const ProposedActionIcon = styled.i`
  color: ${p => p.theme.colors.yellow600};
  flex-shrink: 0;
  font-size: 13px;
  margin-top: 3px;
`;

const ProposedActionTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ProposedActionLabel = styled.span`
  color: ${p => p.theme.colors.yellow700};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ProposedActionToolName = styled.span`
  color: ${p => p.theme.colors.black};
  font-family: monospace;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const ProposedActionArgs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProposedActionExpander = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray700};
  cursor: pointer;
  font-size: 12px;
  padding: 0;

  &:hover {
    color: ${p => p.theme.colors.black};
    text-decoration: underline;
  }
`;

const ProposedActionArgsBlock = styled.pre`
  background: ${p => p.theme.colors.gray100};
  border-radius: 4px;
  color: ${p => p.theme.colors.black};
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
  margin: 0;
  max-height: 200px;
  overflow: auto;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ProposedActionActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-top: 2px;
`;

const ProposedActionMutedState = styled.div`
  color: ${p => p.theme.colors.gray700};
  font-size: 12px;
  font-style: italic;
`;

const ProposedActionForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProposedActionSectionBox = styled.div`
  border-left: 2px solid ${p => p.theme.colors.yellow300};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 10px;
`;

const ProposedActionSectionTitle = styled.span`
  color: ${p => p.theme.colors.gray800};
  font-size: 12px;
  font-weight: 600;
`;

const ProposedActionOptionalTag = styled.span`
  color: ${p => p.theme.colors.gray600};
  font-size: 11px;
  font-weight: 400;
  font-style: italic;
`;

const ProposedActionSectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProposedActionArrayItem = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray200};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const ProposedActionArrayItemHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const ProposedActionArrayItemLabel = styled.span`
  color: ${p => p.theme.colors.gray700};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ProposedActionAddButton = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray700};
  cursor: pointer;
  font-size: 12px;
  padding: 0;

  &:hover:not(:disabled) {
    color: ${p => p.theme.colors.black};
    text-decoration: underline;
  }

  &:disabled {
    color: ${p => p.theme.colors.gray400};
    cursor: default;
  }
`;

const ProposedActionRemoveButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.red400};
  cursor: pointer;
  font-size: 11px;
  padding: 0;

  &:hover:not(:disabled) {
    color: ${p => p.theme.colors.red300};
    text-decoration: underline;
  }

  &:disabled {
    color: ${p => p.theme.colors.gray400};
    cursor: default;
  }
`;

const ProposedActionFieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProposedActionFieldLabel = styled.label`
  color: ${p => p.theme.colors.gray800};
  font-size: 12px;
  font-weight: 600;
`;

const ProposedActionFieldRequired = styled.span`
  color: ${p => p.theme.colors.red400};
`;

const ProposedActionFieldDescription = styled.span`
  color: ${p => p.theme.colors.gray600};
  font-size: 11px;
  line-height: 1.3;
`;

const ProposedActionFieldInput = styled.input`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 4px;
  color: ${p => p.theme.colors.black};
  font-family: inherit;
  font-size: 13px;
  padding: 6px 8px;

  &:focus {
    border-color: ${p => p.theme.colors.green400};
    outline: none;
  }

  &:disabled {
    background: ${p => p.theme.colors.gray100};
    color: ${p => p.theme.colors.gray600};
  }
`;

const ProposedActionFieldSelect = styled.select`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 4px;
  color: ${p => p.theme.colors.black};
  font-family: inherit;
  font-size: 13px;
  padding: 6px 8px;

  &:focus {
    border-color: ${p => p.theme.colors.green400};
    outline: none;
  }

  &:disabled {
    background: ${p => p.theme.colors.gray100};
    color: ${p => p.theme.colors.gray600};
  }
`;

const ProposedActionFieldCheckboxRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const ProposedActionRequiredNote = styled.span`
  color: ${p => p.theme.colors.gray600};
  font-size: 11px;
  font-style: italic;
`;

const ProposedActionErrorBlock = styled.div`
  background: ${p => p.theme.colors.red100};
  border-radius: 4px;
  padding: 8px;
`;

const ProposedActionErrorHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
`;

const ProposedActionErrorIcon = styled.i`
  color: ${p => p.theme.colors.red400};
  font-size: 12px;
`;

const ProposedActionErrorText = styled.span`
  color: ${p => p.theme.colors.red400};
  flex: 1;
  font-size: 12px;
  font-weight: 600;
`;

const ProposedActionErrorExpander = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.red400};
  cursor: pointer;
  font-size: 11px;
  padding: 0;

  &:hover {
    color: ${p => p.theme.colors.red300};
    text-decoration: underline;
  }
`;

const ProposedActionErrorBody = styled.pre`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.red300};
  border-radius: 4px;
  color: ${p => p.theme.colors.black};
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
  margin: 8px 0 0 0;
  max-height: 240px;
  overflow: auto;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-word;
`;
